import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from "@angular/core";
import { Currency } from "@front/m19-api-client";
import { StrategyService } from "@front/m19-injection-token";
import { StrategyEx } from "@front/m19-models";
import { StatsService } from "@front/m19-services";
import { IButtonDirective, MODAL_DATA, ModalOptions, ModalRef, ToasterService } from "@front/m19-ui";
import { DateUtils } from "@front/m19-utils";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import {
  AlgoModeConfig,
  AlgoModeSelectionComponent,
  AlgoModeStrKeys,
  toTargetConfig,
} from "@m19-board/shared/algo-mode-selection/algo-mode-selection.component";
import { UntilDestroy } from "@ngneat/until-destroy";
import { Strategy } from "libs/m19-services/src/lib/api-client/models/Strategy";
import { DateTime } from "luxon";
import { take } from "rxjs";

@UntilDestroy()
@Component({
  selector: "app-modal-content",
  templateUrl: "./switch-algo-mode.component.html",
  imports: [TranslocoDirective, AlgoModeSelectionComponent, IButtonDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SwitchAlgoModeComponent implements OnInit {
  static readonly defaultModalOptions: Omit<ModalOptions<unknown>, "data"> = { size: "sm" };

  private toasterService = inject(ToasterService);
  private statsService = inject(StatsService);

  private readonly strategyService = inject(StrategyService);
  private readonly translocoService = inject(TranslocoService);

  readonly updateLoading = signal(false);

  readonly algoModeConfigInit = signal<Partial<AlgoModeConfig> | undefined>(undefined);
  selectedAlgoModeConfig?: Partial<AlgoModeConfig>;
  readonly suggestedAcosTarget = signal<number | undefined>(undefined);
  endOfCurrentMonth?: Date;

  modalData = inject(MODAL_DATA) as SwitchAlgoModePopupData;
  ref = inject(ModalRef);

  ngOnInit(): void {
    const strategy = this.modalData.strategy;

    this.selectedAlgoModeConfig = {
      algoMode: this.modalData.strategy.algoMode,
      acosTarget: this.modalData.strategy.acosTarget ? Math.round(this.modalData.strategy.acosTarget * 100) : undefined,
      monthlyBudget: this.modalData.strategy.monthlyBudget,
      suggestedBid: this.modalData.strategy.suggestedBid,
      dailyBudget: this.modalData.strategy.dailyBudget,
    };

    this.algoModeConfigInit.set(this.selectedAlgoModeConfig);

    if (!this.selectedAlgoModeConfig.acosTarget) {
      this.statsService
        .getLastSevenDaysStatsPerStrategy(strategy.accountId, strategy.marketplace)
        .pipe(take(1))
        .subscribe((stats) => {
          const strategyStats = stats.get(strategy.strategyId);
          if (strategyStats) {
            this.suggestedAcosTarget.set(Math.round((strategyStats.cost! / strategyStats.adSales!) * 100));
          }
        });
    }
    const today = DateTime.fromISO(this.modalData.strategy.today!);
    this.endOfCurrentMonth = today.endOf("month").toJSDate();
  }

  setAlgoModeConfig(algoModeConfig: Partial<AlgoModeConfig>) {
    this.selectedAlgoModeConfig = algoModeConfig;
  }

  update(): void {
    this.updateLoading.set(true);
    // TODO: we need to factorize the target change logic with the target algo mode modal
    if (this.selectedAlgoModeConfig!.algoMode === this.modalData.strategy.algoMode) {
      const targetName = this.translocoService.translate(AlgoModeStrKeys[this.selectedAlgoModeConfig!.algoMode!].title);
      this.strategyService
        .updateTarget(
          this.modalData.strategy.accountId,
          this.modalData.strategy.marketplace,
          this.modalData.strategy.strategyId!,
          this.modalData.organizationId,
          toTargetConfig(this.selectedAlgoModeConfig!, DateUtils.formatDateMonthForApi(this.endOfCurrentMonth!)),
        )
        .subscribe({
          next: (strategy: Strategy) => {
            this.toasterService.success(`${targetName} updated for strategy ${strategy.name}`);
            this.updateLoading.set(false);
            this.ref.close();
          },
          error: (e) => {
            this.toasterService.error(`Error updating ${targetName}: ${e}`);
            this.updateLoading.set(false);
            this.ref.close();
          },
        });
      return;
    }

    this.strategyService
      .switchStrategyAlgoMode(
        this.modalData.strategy.accountId,
        this.modalData.strategy.marketplace,
        this.modalData.strategy.strategyId,
        this.selectedAlgoModeConfig!.algoMode!,
        this.selectedAlgoModeConfig!.acosTarget ? this.selectedAlgoModeConfig!.acosTarget / 100 : undefined,
        this.selectedAlgoModeConfig!.suggestedBid,
        this.selectedAlgoModeConfig!.dailyBudget,
        this.selectedAlgoModeConfig!.monthlyBudget,
      )
      .subscribe({
        next: (strategy) => {
          this.toasterService.success(`Algo Mode updated for strategy ${strategy.name}`, "Strategy updated");
          this.ref.close();
          this.updateLoading.set(false);
        },
        error: (e) => {
          this.toasterService.error(`Error when updating Algo mode: ${e}`, "Strategy update error");
          this.updateLoading.set(false);
        },
      });
  }
}

export interface SwitchAlgoModePopupData {
  organizationId: number;
  currencyCode: Currency;
  strategy: Pick<
    StrategyEx,
    | "strategyId"
    | "name"
    | "marketplace"
    | "accountId"
    | "algoMode"
    | "acosTarget"
    | "dailyBudget"
    | "monthlyBudget"
    | "suggestedBid"
    | "today"
  >;
}
